import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck, FaChevronLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

const BASE_URL = "https://nissi-india.com";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const BranchMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const params = useParams();

  const isEditMode = useMemo(() => {
    if (!params?.id) return false;
    if (params.id === "new") return false;
    return true;
  }, [params]);

  const editId = isEditMode ? Number(params.id) : 0;

  const [metaLoading, setMetaLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [mainBranchCount, setMainBranchCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stateMode, setStateMode] = useState("select");
  const [districtMode, setDistrictMode] = useState("select");
  const [cityMode, setCityMode] = useState("select");
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    branchName: "",
    branchCode: "",
    address1: "",
    address2: "",
    zipcode: "",
    email: "",
    phone: "",
    mobile: "",
    tinNo: "",
    cstNo: "",
    deliveryAddress: "",
    remarks: "",
    isMainBranch: false,
    autoStnApprove: false,
    countryId: "",
    stateId: "",
    districtId: "",
    cityId: "",
    stateName: "",
    districtName: "",
    cityName: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const setSelectAndClearChildren = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const validateEmail = (value) => {
    if (!value) return true;
    return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value);
  };

  const validateForm = () => {
    const e = {};
    if (!String(formData.branchName || "").trim()) e.branchName = "Branch Name is required";
    if (!String(formData.address1 || "").trim()) e.address1 = "Address1 is required";
    if (!String(formData.countryId || "").trim()) e.countryId = "Country is required";
    if (stateMode === "select") {
      if (!String(formData.stateId || "").trim() || formData.stateId === "Select") {
        e.stateId = "State is required";
      }
    } else if (!String(formData.stateName || "").trim()) {
      e.stateName = "State is required";
    }
    if (!String(formData.zipcode || "").trim()) e.zipcode = "Zipcode is required";
    if (!String(formData.phone || "").trim()) e.phone = "Phone is required";
    if (!validateEmail(String(formData.email || "").trim())) e.email = "Invalid email";
    if (!String(formData.tinNo || "").trim()) e.tinNo = "TIN No is required";
    if (!String(formData.cstNo || "").trim()) e.cstNo = "GST/CST No is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const fetchMeta = async () => {
    try {
      setMetaLoading(true);
      const res = await axios.get(`${BASE_URL}/api/branchMaster/branch_meta`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load branch meta");
        return;
      }
      setCountries(res.data.countries || []);
      setMainBranchCount(Number(res.data.mainBranchCount || 0));
    } catch (err) {
      console.error("branch_meta error:", err);
      toast.error(err.response?.data?.message || "Failed to load branch meta");
    } finally {
      setMetaLoading(false);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      if (!countryId) {
        setStates([]);
        return;
      }
      const res = await axios.get(`${BASE_URL}/api/branchMaster/states`, {
        headers: tenantHeaders(),
        params: tenantParams({ countryId }),
      });
      if (!res.data?.success) return;
      setStates(res.data.states || []);
    } catch (err) {
      console.error("states error:", err);
    }
  };

  const fetchDistricts = async (stateId) => {
    try {
      if (!stateId) {
        setDistricts([]);
        return;
      }
      const res = await axios.get(`${BASE_URL}/api/branchMaster/districts`, {
        headers: tenantHeaders(),
        params: tenantParams({ stateId }),
      });
      if (!res.data?.success) return;
      setDistricts(res.data.districts || []);
    } catch (err) {
      console.error("districts error:", err);
    }
  };

  const fetchCities = async (districtId) => {
    try {
      if (!districtId) {
        setCities([]);
        return;
      }
      const res = await axios.get(`${BASE_URL}/api/branchMaster/cities`, {
        headers: tenantHeaders(),
        params: tenantParams({ districtId }),
      });
      if (!res.data?.success) return;
      setCities(res.data.cities || []);
    } catch (err) {
      console.error("cities error:", err);
    }
  };

  const fetchBranchById = async (id) => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/branchMaster/branch/${id}`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load branch");
        return;
      }

      const d = res.data.data;
      const countryId = d.fld_Countryid ? String(d.fld_Countryid) : "";
      const stateId = d.fld_stateid ? String(d.fld_stateid) : "";
      const districtId = d.fld_Districtid ? String(d.fld_Districtid) : "";
      const cityId = d.fld_Cityid ? String(d.fld_Cityid) : "";

      setFormData((prev) => ({
        ...prev,
        branchName: d.fld_BranchName || "",
        branchCode: d.fld_Branchcode || "",
        address1: d.fld_Address1 || "",
        address2: d.fld_Address2 || "",
        zipcode: d.fld_ZipCode || "",
        email: d.fld_Email || "",
        phone: d.fld_phoneno || "",
        mobile: d.fld_Mobile || "",
        remarks: d.fld_Remarks || "",
        tinNo: d.fld_tinno || "",
        cstNo: d.fld_cstno || "",
        deliveryAddress: d.fld_deliveryaddress || "",
        isMainBranch:
          String(d.fld_ismainbranch).toLowerCase() === "true" ||
          d.fld_ismainbranch === 1 ||
          d.fld_ismainbranch === true,
        autoStnApprove:
          String(d.fld_AutoSTNApprove) === "1" ||
          d.fld_AutoSTNApprove === 1 ||
          d.fld_AutoSTNApprove === true,
        countryId,
        stateId: "",
        districtId: "",
        cityId: "",
        stateName: "",
        districtName: "",
        cityName: "",
      }));

      setStateMode("select");
      setDistrictMode("select");
      setCityMode("select");

      if (countryId) {
        await fetchStates(countryId);
        setFormData((prev) => ({ ...prev, stateId }));
      }
      if (stateId) {
        await fetchDistricts(stateId);
        setFormData((prev) => ({ ...prev, districtId }));
      }
      if (districtId) {
        await fetchCities(districtId);
        setFormData((prev) => ({ ...prev, cityId }));
      }
    } catch (err) {
      console.error("branch fetch error:", err);
      toast.error(err.response?.data?.message || "Failed to load branch");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getFinYearId()) {
      toast.error("Please login and select financial year");
      navigate("/");
      return;
    }
    fetchMeta();
  }, []);

  useEffect(() => {
    if (isEditMode && editId) fetchBranchById(editId);
  }, [isEditMode, editId]);

  const handleCountryChange = async (countryId) => {
    handleInputChange("countryId", countryId);
    setStates([]);
    setDistricts([]);
    setCities([]);
    setStateMode("select");
    setDistrictMode("select");
    setCityMode("select");
    setSelectAndClearChildren({
      stateId: "",
      districtId: "",
      cityId: "",
      stateName: "",
      districtName: "",
      cityName: "",
    });
    if (countryId) await fetchStates(countryId);
  };

  const handleStateChange = async (value) => {
    if (value === "Others") {
      setStateMode("text");
      setDistrictMode("text");
      setCityMode("text");
      setDistricts([]);
      setCities([]);
      setSelectAndClearChildren({
        stateId: "",
        districtId: "",
        cityId: "",
        stateName: "",
        districtName: "",
        cityName: "",
      });
      return;
    }
    setStateMode("select");
    setDistrictMode("select");
    setCityMode("select");
    handleInputChange("stateId", value);
    setDistricts([]);
    setCities([]);
    setSelectAndClearChildren({
      districtId: "",
      cityId: "",
      districtName: "",
      cityName: "",
    });
    if (value) await fetchDistricts(value);
  };

  const handleDistrictChange = async (value) => {
    if (value === "Others") {
      setDistrictMode("text");
      setCityMode("text");
      setCities([]);
      setSelectAndClearChildren({
        districtId: "",
        cityId: "",
        districtName: "",
        cityName: "",
      });
      return;
    }
    setDistrictMode("select");
    setCityMode("select");
    handleInputChange("districtId", value);
    setCities([]);
    setSelectAndClearChildren({ cityId: "", cityName: "" });
    if (value) await fetchCities(value);
  };

  const handleCityChange = (value) => {
    if (value === "Others") {
      setCityMode("text");
      setSelectAndClearChildren({ cityId: "", cityName: "" });
      return;
    }
    setCityMode("select");
    handleInputChange("cityId", value);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        finYearId: Number(getFinYearId()) || undefined,
        branchId: isEditMode ? editId : 0,
        branchName: formData.branchName.trim(),
        branchCode: formData.branchCode.trim(),
        address1: formData.address1.trim(),
        address2: formData.address2.trim(),
        zipcode: formData.zipcode.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        mobile: formData.mobile.trim(),
        remarks: formData.remarks.trim(),
        tinNo: formData.tinNo.trim(),
        cstNo: formData.cstNo.trim(),
        deliveryAddress: formData.deliveryAddress.trim(),
        isMainBranch: !!formData.isMainBranch,
        autoStnApprove: !!formData.autoStnApprove,
        countryId: Number(formData.countryId || 0),
        stateMode,
        districtMode,
        cityMode,
        stateId: stateMode === "select" ? Number(formData.stateId || 0) : 0,
        districtId: districtMode === "select" ? Number(formData.districtId || 0) : 0,
        cityId: cityMode === "select" ? Number(formData.cityId || 0) : 0,
        stateName: stateMode === "text" ? formData.stateName.trim() : "",
        districtName: districtMode === "text" ? formData.districtName.trim() : "",
        cityName: cityMode === "text" ? formData.cityName.trim() : "",
      };

      const res = await axios.post(`${BASE_URL}/api/branchMaster/branch_save`, payload, {
        headers: tenantHeaders(),
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }
      toast.success(res.data?.message || "Saved Successfully");
      navigate("/adminMaster/branch-listing");
    } catch (err) {
      console.error("save error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const disableMainBranchCheckbox = useMemo(() => {
    if (isEditMode && formData.isMainBranch) return false;
    return mainBranchCount === 1;
  }, [mainBranchCount, isEditMode, formData.isMainBranch]);

  const inputClass = (hasError) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
      hasError ? "border-red-500" : ""
    } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`;

  const labelClass = `text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const selectClass = (hasError) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
      hasError ? "border-red-500" : ""
    } ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`;

  const errorText = (msg) => (msg ? <p className="text-red-500 text-sm mt-1">{msg}</p> : null);
  const onlyDigits = (v) => String(v || "").replace(/[^\d]/g, "");

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{isEditMode ? "BRANCH MASTER (EDIT)" : "BRANCH MASTER (ADD)"}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate("/adminMaster/branch-listing")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaChevronLeft />
                Back
              </button>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {isEditMode ? `Editing ID: ${editId}` : "New Branch"}
              </span>
            </div>
            <button
              onClick={handleSubmit}
              disabled={saving || loading || metaLoading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
            >
              <FaCheck />
              {saving ? "Saving..." : "Submit"}
            </button>
          </div>

          {(metaLoading || loading) && (
            <div className={`mb-6 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>Loading...</div>
          )}

          <div className={`rounded-xl border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Branch Name <span className="text-red-500">*</span></label>
                  <input value={formData.branchName} onChange={(e) => handleInputChange("branchName", e.target.value)} disabled={saving} className={inputClass(!!errors.branchName)} placeholder="Enter Branch Name" />
                  {errorText(errors.branchName)}
                </div>
                <div>
                  <label className={labelClass}>Branch Code</label>
                  <input value={formData.branchCode} onChange={(e) => handleInputChange("branchCode", e.target.value)} disabled={saving} className={inputClass(false)} placeholder="Enter Branch Code" />
                </div>
                <div>
                  <label className={labelClass}>Address 1 <span className="text-red-500">*</span></label>
                  <input value={formData.address1} onChange={(e) => handleInputChange("address1", e.target.value)} disabled={saving} className={inputClass(!!errors.address1)} placeholder="Enter Address 1" />
                  {errorText(errors.address1)}
                </div>
                <div>
                  <label className={labelClass}>Address 2</label>
                  <input value={formData.address2} onChange={(e) => handleInputChange("address2", e.target.value)} disabled={saving} className={inputClass(false)} placeholder="Enter Address 2" />
                </div>
                <div>
                  <label className={labelClass}>Country <span className="text-red-500">*</span></label>
                  <select value={formData.countryId} onChange={(e) => handleCountryChange(e.target.value)} disabled={saving} className={selectClass(!!errors.countryId)}>
                    <option value="">Select</option>
                    {countries.map((c) => (
                      <option key={c.fld_countryid} value={String(c.fld_countryid)}>{c.fld_countryname}</option>
                    ))}
                  </select>
                  {errorText(errors.countryId)}
                </div>
                <div>
                  <label className={labelClass}>State <span className="text-red-500">*</span></label>
                  {stateMode === "select" ? (
                    <>
                      <select value={formData.stateId} onChange={(e) => handleStateChange(e.target.value)} disabled={saving || !formData.countryId} className={selectClass(!!errors.stateId)}>
                        <option value="">Select</option>
                        {states.map((s) => (
                          <option key={s.fld_stateid} value={String(s.fld_stateid)}>{s.fld_statename}</option>
                        ))}
                        {formData.countryId ? <option value="Others">Others</option> : null}
                      </select>
                      {errorText(errors.stateId)}
                    </>
                  ) : (
                    <>
                      <input value={formData.stateName} onChange={(e) => handleInputChange("stateName", e.target.value)} disabled={saving} className={inputClass(!!errors.stateName)} placeholder="Enter State" />
                      {errorText(errors.stateName)}
                    </>
                  )}
                </div>
                <div>
                  <label className={labelClass}>District</label>
                  {districtMode === "select" ? (
                    <select value={formData.districtId} onChange={(e) => handleDistrictChange(e.target.value)} disabled={saving || stateMode !== "select" || !formData.stateId} className={selectClass(false)}>
                      <option value="">Select</option>
                      {districts.map((d) => (
                        <option key={d.fld_districtid} value={String(d.fld_districtid)}>{d.fld_districtname}</option>
                      ))}
                      {formData.stateId ? <option value="Others">Others</option> : null}
                    </select>
                  ) : (
                    <input value={formData.districtName} onChange={(e) => handleInputChange("districtName", e.target.value)} disabled={saving} className={inputClass(false)} placeholder="Enter District" />
                  )}
                </div>
                <div>
                  <label className={labelClass}>City</label>
                  {cityMode === "select" ? (
                    <select value={formData.cityId} onChange={(e) => handleCityChange(e.target.value)} disabled={saving || districtMode !== "select" || !formData.districtId} className={selectClass(false)}>
                      <option value="">Select</option>
                      {cities.map((c) => (
                        <option key={c.fld_cityid} value={String(c.fld_cityid)}>{c.fld_cityname}</option>
                      ))}
                      {formData.districtId ? <option value="Others">Others</option> : null}
                    </select>
                  ) : (
                    <input value={formData.cityName} onChange={(e) => handleInputChange("cityName", e.target.value)} disabled={saving} className={inputClass(false)} placeholder="Enter City" />
                  )}
                </div>
                <div>
                  <label className={labelClass}>Zipcode <span className="text-red-500">*</span></label>
                  <input value={formData.zipcode} onChange={(e) => handleInputChange("zipcode", onlyDigits(e.target.value).slice(0, 6))} disabled={saving} className={inputClass(!!errors.zipcode)} placeholder="Enter Zipcode" maxLength={6} inputMode="numeric" />
                  {errorText(errors.zipcode)}
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} disabled={saving} className={inputClass(!!errors.email)} placeholder="Enter Email" />
                  {errorText(errors.email)}
                </div>
                <div>
                  <label className={labelClass}>Phone <span className="text-red-500">*</span></label>
                  <input value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} disabled={saving} className={inputClass(!!errors.phone)} placeholder="Enter Phone" maxLength={15} />
                  {errorText(errors.phone)}
                </div>
                <div>
                  <label className={labelClass}>Mobile</label>
                  <input value={formData.mobile} onChange={(e) => handleInputChange("mobile", onlyDigits(e.target.value).slice(0, 10))} disabled={saving} className={inputClass(false)} placeholder="Enter Mobile" maxLength={10} inputMode="numeric" />
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={!!formData.isMainBranch} onChange={(e) => handleInputChange("isMainBranch", e.target.checked)} disabled={saving || disableMainBranchCheckbox} className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`} />
                  <label className={labelClass}>Is Main Branch</label>
                  {disableMainBranchCheckbox && !formData.isMainBranch && (
                    <span className={`text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>(Main branch already exists)</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={!!formData.autoStnApprove} onChange={(e) => handleInputChange("autoStnApprove", e.target.checked)} disabled={saving} className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`} />
                  <label className={labelClass}>Auto STN Approve</label>
                </div>
                <div>
                  <label className={labelClass}>TIN No <span className="text-red-500">*</span></label>
                  <input value={formData.tinNo} onChange={(e) => handleInputChange("tinNo", e.target.value)} disabled={saving} className={inputClass(!!errors.tinNo)} placeholder="Enter TIN No" />
                  {errorText(errors.tinNo)}
                </div>
                <div>
                  <label className={labelClass}>GST No <span className="text-red-500">*</span></label>
                  <input value={formData.cstNo} onChange={(e) => handleInputChange("cstNo", e.target.value)} disabled={saving} className={inputClass(!!errors.cstNo)} placeholder="Enter GST No" />
                  {errorText(errors.cstNo)}
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Delivery Address</label>
                  <textarea value={formData.deliveryAddress} onChange={(e) => handleInputChange("deliveryAddress", e.target.value)} disabled={saving} className={`${inputClass(false)} min-h-[90px]`} placeholder="Enter Delivery Address" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelClass}>Remarks</label>
                  <textarea value={formData.remarks} onChange={(e) => handleInputChange("remarks", e.target.value)} disabled={saving} className={`${inputClass(false)} min-h-[90px]`} placeholder="Enter Remarks" />
                </div>
              </div>
            </div>
          </div>

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default BranchMaster;